import { RedirectToSignIn } from '@clerk/clerk-react';
import { IS_DEV_AUTH, useDevAuth } from './hooks/useDevAuth';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Snackbar,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import './App.css';
import CheckIcon from '@mui/icons-material/Check';
import EssayContent from './components/EssayContent';
import LabelForm from './components/LabelForm';
import { AssignmentsQueueHeader } from './components/AssignmentsQueueHeader';
import { LabellerAssignmentsDrawer } from './components/LabellerAssignmentsDrawer';
import Loading from './components/Loading';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useSignOut } from './hooks/useSignOut';
import { useEssayUtils } from './hooks/useEssayUtils';
import { useLabels } from './hooks/useLabels';
import { useNotification } from './hooks/useNotification';
import SearchableSelect, { SearchableOption } from './components/common/SearchableSelect';
import * as api from './services/api';
import { theme } from './theme';
import {
  Criteria,
  CriteriaPurpose,
  CriterionTranscriptionAssignment,
  Essay,
  EssayPurpose,
  Policy,
  Purpose,
  Transcription,
} from './types';

function App() {
  const { isSignedIn } = useDevAuth();
  const signOut = useSignOut();
  const { currentUser, isLoading: isLoadingUser, error: userError } = useCurrentUser();

  // Basic state
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [selectedCriteria, setSelectedCriteria] = useState<Criteria | null>(null);

  // Purpose-related state
  const [purposes, setPurposes] = useState<Purpose[]>([]);
  const [essayPurposes, setEssayPurposes] = useState<EssayPurpose[]>([]);
  const [criteriaPurposes, setCriteriaPurposes] = useState<CriteriaPurpose[]>([]);

  // Essays state
  const [essays, setEssays] = useState<Essay[]>([]);

  // User and assignments state
  const [criterionTranscriptionAssignments, setCriterionTranscriptionAssignments] = useState<
    CriterionTranscriptionAssignment[]
  >([]);
  const [isAssignmentsDrawerOpen, setIsAssignmentsDrawerOpen] = useState(false);

  // Custom hooks
  const { notification, setNotification, hideNotification } = useNotification();
  const { getFragments } = useEssayUtils();

  const granularity = selectedCriteria?.granularity ?? 'sentence';

  const {
    labels,
    isTranscriptionUnidentifiable,
    selectedFragment,
    labelType,
    reason,
    setReason,
    isEditing,
    getLabelForFragment,
    handleFragmentClick,
    handleDeleteLabel,
    handleSubmitLabel,
    handleSubmitWithType,
    handleMarkAsUnidentifiable,
    handleMarkAsIdentifiable,
    handleToggleAllPositive,
    handleToggleAllNegative,
    resetForm,
    isLoading,
  } = useLabels(
    selectedTranscription,
    selectedCriteria,
    setNotification,
    getFragments,
    granularity,
    currentUser
  );

  const refetchPoliciesAndCriteria = useCallback(async () => {
    try {
      const [policiesData, criteriaData] = await Promise.all([
        api.fetchPolicies(),
        api.fetchCriteria(),
      ]);
      setPolicies(policiesData);
      setCriteria(criteriaData);
    } catch (error) {
      setNotification({
        open: true,
        message: 'Error refreshing policy/criteria data',
        severity: 'error',
      });
    }
  }, [setNotification]);

  // Load assignments for all users so we know both your and others' done state
  useEffect(() => {
    if (!currentUser) return;

    const fetchAssignments = async () => {
      try {
        const assignmentsData = await api.fetchCriterionTranscriptionAssignments();
        setCriterionTranscriptionAssignments(assignmentsData);
      } catch (error) {
        console.error('Error loading assignments:', error);
        setNotification({
          open: true,
          message: 'Error loading assignments',
          severity: 'error',
        });
      }
    };

    fetchAssignments();
  }, [currentUser, setNotification]);

  // Load transcriptions, policies, criteria, and purposes
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const [
          transcriptionsData,
          policiesData,
          criteriaData,
          purposesData,
          essayPurposesData,
          criteriaPurposesData,
          essaysData,
        ] = await Promise.all([
          api.fetchTranscriptions(),
          api.fetchPolicies(),
          api.fetchCriteria(),
          api.fetchPurposes(),
          api.fetchEssayPurposes(),
          api.fetchCriteriaPurposes(),
          api.fetchEssays(),
        ]);

        setTranscriptions(transcriptionsData);
        setPolicies(policiesData);
        setCriteria(criteriaData);
        setPurposes(purposesData);
        setEssayPurposes(essayPurposesData);
        setCriteriaPurposes(criteriaPurposesData);
        setEssays(essaysData);

        // Default selections will be set after filtering by assignments
      } catch (error) {
        console.error('Error loading initial data:', error);
        setNotification({
          open: true,
          message: 'Error loading data',
          severity: 'error',
        });
      }
    };

    fetchItems();
  }, [setNotification]);

  // Helper functions for assignments and filtering
  const getAssignedTranscriptions = useCallback(() => {
    if (!currentUser || criterionTranscriptionAssignments.length === 0) return [];
    const assignedTranscriptionIds = criterionTranscriptionAssignments
      .filter(a => a.userId === currentUser.id)
      .map(assignment => assignment.transcriptionId);
    return transcriptions.filter(transcription =>
      assignedTranscriptionIds.includes(transcription.id)
    );
  }, [currentUser, transcriptions, criterionTranscriptionAssignments]);

  const getAssignedCriteria = useCallback(() => {
    if (!currentUser || criterionTranscriptionAssignments.length === 0) return [];
    const assignedCriteriaIds = criterionTranscriptionAssignments
      .filter(a => a.userId === currentUser.id)
      .map(assignment => assignment.criteriaId);
    // Ensure unique criteria IDs
    const uniqueAssignedCriteriaIds = Array.from(new Set(assignedCriteriaIds));
    return criteria.filter(criterion => uniqueAssignedCriteriaIds.includes(criterion.id));
  }, [currentUser, criteria, criterionTranscriptionAssignments]);

  // Get transcriptions filtered by the currently selected criteria's purposes
  const getFilteredTranscriptionsForSelectedCriteria = useCallback(() => {
    // Guard clauses for essential data
    if (!currentUser || !selectedCriteria) {
      // If no user or no criteria selected, no specific transcriptions to show for this context
      return [];
    }
    // If there are no assignments loaded at all, can't proceed
    if (criterionTranscriptionAssignments.length === 0) {
      return [];
    }

    // 1. Filter assignments to find those matching the current user AND the selected criteria.
    const assignmentsForUserAndCriteria = criterionTranscriptionAssignments.filter(
      assignment =>
        assignment.userId === currentUser.id && assignment.criteriaId === selectedCriteria.id
    );

    // If no assignments exist for this specific user/criteria combination
    if (assignmentsForUserAndCriteria.length === 0) {
      return [];
    }

    // Extract the IDs of transcriptions assigned under the current user/criteria
    const assignedTranscriptionIds = assignmentsForUserAndCriteria.map(
      assignment => assignment.transcriptionId
    );

    // 2. Get the actual transcription objects for these IDs from the master list.
    let candidateTranscriptions = transcriptions.filter(transcription =>
      assignedTranscriptionIds.includes(transcription.id)
    );

    // 3. Further filter these candidate transcriptions by purpose,
    // if purposes are defined for the selected criteria.
    const criteriaPurposeIds = criteriaPurposes
      .filter(cp => cp.criteriaId === selectedCriteria.id)
      .map(cp => cp.purposeId);

    // If the selected criteria has no specific purposes associated with it,
    // then all transcriptions assigned to this user for this criteria (already filtered) are considered valid.
    if (criteriaPurposeIds.length === 0) {
      return candidateTranscriptions;
    }

    // Otherwise, filter by matching essay purposes.
    const finalFilteredTranscriptions = candidateTranscriptions.filter(transcription =>
      essayPurposes.some(
        ep => ep.essayId === transcription.essayId && criteriaPurposeIds.includes(ep.purposeId)
      )
    );

    return finalFilteredTranscriptions;
  }, [
    currentUser,
    selectedCriteria,
    criterionTranscriptionAssignments,
    transcriptions,
    criteriaPurposes,
    essayPurposes,
  ]);

  // Computed values
  const userAssignments = useMemo(
    () =>
      currentUser ? criterionTranscriptionAssignments.filter(a => a.userId === currentUser.id) : [],
    [criterionTranscriptionAssignments, currentUser]
  );
  const remainingAssignmentsCount = useMemo(
    () => userAssignments.filter(a => !a.done).length,
    [userAssignments]
  );

  const handleSelectAssignmentFromDrawer = useCallback(
    (criterion: Criteria, transcription: Transcription) => {
      setSelectedCriteria(criterion);
      setSelectedTranscription(transcription);
      resetForm();
    },
    [resetForm]
  );

  const handleAssignmentsUpdatedFromDrawer = useCallback(
    (updatedAssignments: CriterionTranscriptionAssignment[]) => {
      setCriterionTranscriptionAssignments(updatedAssignments);
    },
    []
  );

  const currentAssignment = useMemo(() => {
    if (!currentUser || !selectedCriteria || !selectedTranscription) return null;
    return (
      criterionTranscriptionAssignments.find(
        a =>
          a.userId === currentUser.id &&
          a.criteriaId === selectedCriteria.id &&
          a.transcriptionId === selectedTranscription.id
      ) ?? null
    );
  }, [currentUser, selectedCriteria, selectedTranscription, criterionTranscriptionAssignments]);

  const [isUpdatingCurrentAssignmentDone, setIsUpdatingCurrentAssignmentDone] = useState(false);

  const handleToggleCurrentAssignmentDone = useCallback(async () => {
    if (!currentAssignment) return;
    const nextDone = !currentAssignment.done;
    setIsUpdatingCurrentAssignmentDone(true);
    try {
      const updated = await api.updateAssignmentDone(currentAssignment.id, nextDone);
      setCriterionTranscriptionAssignments(prev =>
        prev.map(a => (a.id === updated.id ? updated : a))
      );
      setNotification({
        open: true,
        message: `Assignment marked as ${nextDone ? 'done' : 'not done'}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to update assignment status:', err);
      setNotification({
        open: true,
        message: 'Failed to update assignment status',
        severity: 'error',
      });
    } finally {
      setIsUpdatingCurrentAssignmentDone(false);
    }
  }, [currentAssignment, setNotification]);

  const userTranscriptions = useMemo(
    () => getAssignedTranscriptions(),
    [getAssignedTranscriptions]
  );
  const userCriteria = useMemo(() => getAssignedCriteria(), [getAssignedCriteria]);

  const filteredTranscriptions = useMemo(
    () => getFilteredTranscriptionsForSelectedCriteria(),
    [getFilteredTranscriptionsForSelectedCriteria]
  );

  const transcriptionOptions: SearchableOption[] = useMemo(() => {
    if (filteredTranscriptions.length === 0) return [];
    return filteredTranscriptions
      .map(transcription => {
        const userAssignment =
          selectedCriteria && currentUser
            ? criterionTranscriptionAssignments.find(
                a =>
                  a.transcriptionId === transcription.id &&
                  a.criteriaId === selectedCriteria.id &&
                  a.userId === currentUser.id
              )
            : null;
        const isDoneByCurrentUser = userAssignment?.done ?? false;

        let countDoneByOthers = 0;
        if (selectedCriteria && currentUser) {
          countDoneByOthers = criterionTranscriptionAssignments.filter(
            a =>
              a.transcriptionId === transcription.id &&
              a.criteriaId === selectedCriteria.id &&
              a.userId !== currentUser.id &&
              a.done
          ).length;
        }

        const purposeIds = criteriaPurposes
          .filter(cp => cp.criteriaId === selectedCriteria?.id)
          .map(cp => cp.purposeId);

        const essayPurposeId = essayPurposes.find(
          ep => ep.essayId === transcription.essayId && purposeIds.includes(ep.purposeId)
        )?.purposeId;

        const purposeName = essayPurposeId
          ? purposes.find(p => p.id === essayPurposeId)?.name || 'Unknown Purpose'
          : 'No Purpose';

        const essay = essays.find(e => e.id === transcription.essayId);

        const displayText = essay
          ? `${purposeName} - ${essay.learn_cycle_batch} - ${essay.paper_number} - ${transcription.versionName}`
          : `${purposeName} - ${transcription.versionName} - ID: ${transcription.id.substring(0, 8)}`;

        return {
          transcription,
          isDone: isDoneByCurrentUser,
          displayText,
          countDoneByOthers,
        };
      })
      .sort((a, b) => a.displayText.localeCompare(b.displayText))
      .map(({ transcription, isDone, displayText, countDoneByOthers }) => ({
        value: transcription.id,
        label: displayText,
        renderContent: (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: isDone ? 'bold' : 'normal' }}>
              {displayText}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Done by you: {isDone ? '\u2713' : '\u2717'}
              <Box component="span" sx={{ mx: 0.5 }} />
              Done by others: {countDoneByOthers > 0 ? '\u2713' : '\u2717'}
            </Typography>
          </Box>
        ),
      }));
  }, [
    filteredTranscriptions,
    selectedCriteria,
    currentUser,
    criterionTranscriptionAssignments,
    criteriaPurposes,
    essayPurposes,
    purposes,
    essays,
  ]);

  // Set default selections after all data is loaded and filtered
  useEffect(() => {
    if (
      !currentUser ||
      transcriptions.length === 0 ||
      criteria.length === 0 ||
      criterionTranscriptionAssignments.length === 0
    )
      return;

    const assignedTranscriptionIds = criterionTranscriptionAssignments
      .filter(a => a.userId === currentUser.id)
      .map(assignment => assignment.transcriptionId);
    const userOverallTranscriptions = transcriptions.filter(t =>
      assignedTranscriptionIds.includes(t.id)
    );

    const assignedCriteriaIds = criterionTranscriptionAssignments
      .filter(a => a.userId === currentUser.id)
      .map(assignment => assignment.criteriaId);
    const uniqueAssignedCriteriaIds = Array.from(new Set(assignedCriteriaIds));
    const userOverallCriteria = criteria.filter(c => uniqueAssignedCriteriaIds.includes(c.id));

    // Set default CRITERIA if none is selected
    if (userOverallCriteria.length > 0 && !selectedCriteria) {
      setSelectedCriteria(userOverallCriteria[0]);
    }

    // Set default TRANSCRIPTION only if no criteria is selected yet, and no transcription is selected.
    if (userOverallTranscriptions.length > 0 && !selectedTranscription && !selectedCriteria) {
      setSelectedTranscription(userOverallTranscriptions[0]);
    }

    const userPolicies = policies.filter(policy =>
      userOverallCriteria.find(c => c.id === policy.criteriaId)
    );
    if (userPolicies.length > 0 && !selectedCriteria) {
      const firstCriteriaForPolicy = userOverallCriteria.find(
        c => c.id === userPolicies[0].criteriaId
      );
      if (firstCriteriaForPolicy) {
        setSelectedCriteria(firstCriteriaForPolicy);
      }
    }
  }, [
    transcriptions,
    criteria,
    policies,
    criterionTranscriptionAssignments,
    currentUser,
    selectedTranscription,
    selectedCriteria,
  ]);

  // Update selected transcription based on the current context defined by selectedCriteria
  useEffect(() => {
    if (!currentUser) return;

    const currentContextTranscriptions = filteredTranscriptions;

    if (currentContextTranscriptions.length > 0) {
      const isSelectedTranscriptionInContext =
        selectedTranscription &&
        currentContextTranscriptions.some((t: Transcription) => t.id === selectedTranscription.id);

      if (!isSelectedTranscriptionInContext) {
        // Not in context (or selectedTranscription is null), so pick the first from context.
        // Only update if it's a different transcription to avoid needless re-renders/form resets.
        if (selectedTranscription?.id !== currentContextTranscriptions[0].id) {
          setSelectedTranscription(currentContextTranscriptions[0]);
          resetForm(); // Content changed
        }
      }
    } else {
      // Context is empty (e.g., selected criteria has no matching transcriptions),
      // so selectedTranscription should be null.
      if (selectedTranscription !== null) {
        setSelectedTranscription(null);
        resetForm();
      }
    }
  }, [currentUser, selectedCriteria, filteredTranscriptions, selectedTranscription, resetForm]);

  // Keep policy up to date with the latest policy for the selected criteria
  useEffect(() => {
    if (!selectedCriteria || criteria.length === 0 || policies.length === 0) return;

    // Find the full criteria object (in case the selectedCriteria state is stale)
    const currentCriteriaFull = criteria.find(c => c.id === selectedCriteria.id);
    if (!currentCriteriaFull) return;

    // If the policyId in the selectedCriteria state doesn't match the latest one in the criteria list
    if (selectedCriteria.policyId !== currentCriteriaFull.policyId) {
      // Update the selectedCriteria state to reflect the latest criteria data (including policyId)
      setSelectedCriteria(currentCriteriaFull);
      resetForm();
    }
  }, [selectedCriteria, criteria, policies, resetForm]);

  // Handle transcription navigation
  const goToNextTranscription = useCallback(() => {
    if (!selectedTranscription || !currentUser) return;

    if (filteredTranscriptions.length === 0) return;

    const currentIndex = filteredTranscriptions.findIndex(t => t.id === selectedTranscription.id);
    const nextIndex = (currentIndex + 1) % filteredTranscriptions.length;

    setSelectedTranscription(filteredTranscriptions[nextIndex]);
    resetForm();
  }, [selectedTranscription, currentUser, resetForm, filteredTranscriptions]);

  const goToPreviousTranscription = useCallback(() => {
    if (!selectedTranscription || !currentUser) return;

    if (filteredTranscriptions.length === 0) return;

    const currentIndex = filteredTranscriptions.findIndex(t => t.id === selectedTranscription.id);
    const prevIndex =
      (currentIndex - 1 + filteredTranscriptions.length) % filteredTranscriptions.length;

    setSelectedTranscription(filteredTranscriptions[prevIndex]);
    resetForm();
  }, [selectedTranscription, currentUser, resetForm, filteredTranscriptions]);

  // Navigate to admin interface
  const navigateToAdmin = () => {
    window.location.href = '/admin';
  };

  // Set up keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys if not typing in an input field
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        goToPreviousTranscription();
      } else if (event.key === 'ArrowRight') {
        goToNextTranscription();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPreviousTranscription, goToNextTranscription]);

  // Check if policy is new or updated since the last label for that criteria
  const isPolicyUpdatedSinceLastLabel = useCallback(
    (criteriaId: string): boolean => {
      if (!currentUser) return false;

      // Find the policy for this criteria
      const policyId = criteria.find(c => c.id === criteriaId)?.policyId;
      const policy = policies.find(p => p.id === policyId);
      if (!policy) return false;

      // Find the latest label by the current user for this criteria
      const userLabelsForCriteria = labels
        .filter(label => label.criteriaId === criteriaId && label.createdBy === currentUser.id)
        .sort((a, b) => {
          // Sort by creation date in descending order (newest first)
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });

      // If the user has no labels for this criteria, the policy is not considered updated
      if (userLabelsForCriteria.length === 0) return false;

      // Get the most recent label's creation date
      const latestLabel = userLabelsForCriteria[0];
      const latestLabelDate = new Date(latestLabel.createdDate);

      // Compare with policy creation date
      const policyDate = new Date(policy.creationDate);

      // Return true if policy was created/updated after the latest label
      return policyDate > latestLabelDate;
    },
    [currentUser, policies, labels, criteria]
  );

  const criteriaOptions: SearchableOption[] = useMemo(() => {
    if (userCriteria.length === 0) return [];
    return userCriteria
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .flatMap(item => {
        const policyId = criteria.find(c => c.id === item.id)?.policyId;
        const policy = policies.find(p => p.id === policyId);
        if (!policy) return [];

        const userLabeledTranscriptionIds = new Set(
          labels
            .filter(label => label.criteriaId === item.id && label.createdBy === currentUser!.id)
            .map(label => label.transcriptionId)
        );
        const userPolicyTranscriptionCount = userLabeledTranscriptionIds.size;
        const totalLabeledTranscriptionIds = new Set(
          labels.filter(label => label.criteriaId === item.id).map(label => label.transcriptionId)
        );
        const totalPolicyTranscriptionCount = totalLabeledTranscriptionIds.size;
        const isPolicyUpdated = isPolicyUpdatedSinceLastLabel(item.id);

        return [
          {
            value: item.id,
            label: `${item.name} [${item.granularity}]`,
            renderContent: (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {item.isArchived && (
                  <Chip
                    label="Archived"
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1, flexShrink: 0 }}
                  />
                )}
                {isPolicyUpdated && (
                  <Chip
                    label="Updated Policy"
                    color="primary"
                    size="small"
                    sx={{ mr: 1, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography variant="body2">
                    {item.name} [{item.granularity}]
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Your labels: {userPolicyTranscriptionCount}
                    <Box component="span" sx={{ mx: 0.3 }} />
                    Total labels: {totalPolicyTranscriptionCount}
                  </Typography>
                </Box>
              </Box>
            ),
          },
        ];
      });
  }, [userCriteria, criteria, policies, labels, currentUser, isPolicyUpdatedSinceLastLabel]);

  if (!isSignedIn) return IS_DEV_AUTH ? null : <RedirectToSignIn />;

  if (isLoadingUser) return <Loading />;

  if (userError) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {userError}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => window.location.reload()}>
                Refresh page
              </Button>
              <Button variant="contained" onClick={signOut}>
                Sign out
              </Button>
            </Box>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth={false} className="app-container">
        {!currentUser ? (
          <Paper className="empty-state" sx={{ mt: 4, p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Loading user information...
            </Typography>
            <Typography>Please wait while we load your account details.</Typography>
          </Paper>
        ) : (
          <>
            <AssignmentsQueueHeader
              remainingCount={remainingAssignmentsCount}
              totalCount={userAssignments.length}
              currentUser={currentUser}
              onOpenQueue={() => setIsAssignmentsDrawerOpen(true)}
              onNavigateToAdmin={navigateToAdmin}
            />

            {/* Selection Controls */}
            <Paper className="selection-controls">
              <Grid container spacing={2}>
                {/* Criteria Selection */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="criteria-select-label">Criteria</InputLabel>
                    <SearchableSelect
                      labelId="criteria-select-label"
                      value={selectedCriteria?.id || ''}
                      label="Criteria"
                      onChange={e => {
                        const criteriaId = e.target.value as string;
                        const criteriaItem = criteria.find(c => c.id === criteriaId);
                        if (criteriaItem) {
                          setSelectedCriteria(criteriaItem);
                          resetForm();
                        }
                      }}
                      onOpen={refetchPoliciesAndCriteria}
                      options={criteriaOptions}
                    />
                  </FormControl>
                </Grid>

                {/* Transcription Selection */}
                <Grid item xs={12} md={6}>
                  <Box className="essay-selection-container">
                    <FormControl className="essay-select">
                      <InputLabel id="transcription-select-label">Transcription</InputLabel>
                      <SearchableSelect
                        labelId="transcription-select-label"
                        value={
                          selectedTranscription &&
                          filteredTranscriptions.some(t => t.id === selectedTranscription.id)
                            ? selectedTranscription.id
                            : ''
                        }
                        label="Transcription"
                        onChange={e => {
                          const transcriptionId = e.target.value as string;
                          const transcription = transcriptions.find(t => t.id === transcriptionId);
                          if (transcription) {
                            setSelectedTranscription(transcription);
                            resetForm();
                          }
                        }}
                        options={transcriptionOptions}
                      />
                    </FormControl>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mx: 1 }}
                      onClick={goToPreviousTranscription}
                    >
                      Previous (←)
                    </Button>
                    <Button variant="outlined" size="small" onClick={goToNextTranscription}>
                      Next (→)
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {userTranscriptions.length === 0 || userCriteria.length === 0 ? (
              <Paper className="empty-state">
                <Typography variant="h5" gutterBottom>
                  {userTranscriptions.length === 0 && userCriteria.length === 0
                    ? 'No transcriptions and criteria assigned to you'
                    : userTranscriptions.length === 0
                      ? 'No transcriptions assigned to you'
                      : 'No criteria assigned to you'}
                </Typography>
                <Typography>Please contact an administrator to get assignments.</Typography>
              </Paper>
            ) : (
              <Grid container spacing={2} className="grid-container">
                {/* Labelling Policy */}
                <Grid item xs={12} md={6} className="grid-item">
                  <Paper className="paper">
                    <Box className="header-box">
                      <Typography variant="h6">Labelling Policy</Typography>
                    </Box>
                    <Box className="content-box">
                      {selectedCriteria ? (
                        <Box className="markdown-container">
                          <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                            {policies.find(p => p.id === selectedCriteria?.policyId)?.text ||
                              'Policy text not found...'}
                          </ReactMarkdown>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 3,
                            width: '100%',
                          }}
                        >
                          <Typography
                            variant="h5"
                            color="error"
                            gutterBottom
                            sx={{ fontWeight: 'bold' }}
                          >
                            No Policy Available
                          </Typography>
                          <Typography variant="body1" color="text.primary">
                            Please select a criteria to view its policy.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Transcription Content */}
                <Grid item xs={12} md={6} className="grid-item">
                  <Paper className="paper">
                    <Box className="header-box" sx={{ mb: 1.25 }}>
                      <Typography variant="h6">Transcription</Typography>
                      {currentAssignment && (
                        <Button
                          variant={currentAssignment.done ? 'contained' : 'outlined'}
                          color="success"
                          size="small"
                          onClick={handleToggleCurrentAssignmentDone}
                          disabled={isUpdatingCurrentAssignmentDone}
                          startIcon={
                            currentAssignment.done ? <CheckIcon fontSize="small" /> : undefined
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 0.3,
                            px: 1.25,
                          }}
                        >
                          {currentAssignment.done ? 'Done' : 'Mark as done'}
                        </Button>
                      )}
                    </Box>

                    {selectedTranscription && filteredTranscriptions.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.5,
                          flexWrap: 'nowrap',
                          overflowX: 'auto',
                        }}
                      >
                        {isTranscriptionUnidentifiable ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleMarkAsIdentifiable}
                            size="small"
                            sx={{
                              py: 0.25,
                              px: 1,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Mark as identifiable
                          </Button>
                        ) : (
                          <>
                            {granularity !== 'essay' && (
                              <>
                                <Button
                                  variant="outlined"
                                  className="positive-button"
                                  onClick={handleToggleAllPositive}
                                  size="small"
                                  sx={{
                                    py: 0.25,
                                    px: 1,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    opacity: isLoading ? 0.6 : 1,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                  }}
                                  disabled={isLoading}
                                >
                                  All positive
                                </Button>
                                <Button
                                  variant="outlined"
                                  className="negative-button"
                                  onClick={handleToggleAllNegative}
                                  size="small"
                                  sx={{
                                    py: 0.25,
                                    px: 1,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    opacity: isLoading ? 0.6 : 1,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                  }}
                                  disabled={isLoading}
                                >
                                  All negative
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outlined"
                              color="warning"
                              onClick={handleMarkAsUnidentifiable}
                              size="small"
                              sx={{
                                py: 0.25,
                                px: 1,
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              All unidentifiable
                            </Button>
                          </>
                        )}
                      </Box>
                    )}

                    {selectedTranscription && filteredTranscriptions.length > 0 ? (
                      <>
                        <EssayContent
                          fragments={getFragments(selectedTranscription.content, granularity)}
                          selectedFragmentIndex={selectedFragment?.index ?? null}
                          isTranscriptionUnidentifiable={isTranscriptionUnidentifiable}
                          handleFragmentClick={handleFragmentClick}
                          getLabelForFragment={getLabelForFragment}
                          granularity={granularity}
                        />

                        {!isTranscriptionUnidentifiable && selectedCriteria && (
                          <LabelForm
                            selectedFragment={selectedFragment}
                            labelType={labelType}
                            reason={reason}
                            setReason={setReason}
                            isEditing={isEditing}
                            handleSubmitLabel={handleSubmitLabel}
                            handleSubmitWithType={handleSubmitWithType}
                            handleDeleteLabel={handleDeleteLabel}
                            handleCancelEdit={resetForm}
                            granularity={granularity}
                          />
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          p: 3,
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="h5"
                          color="error"
                          gutterBottom
                          sx={{ fontWeight: 'bold' }}
                        >
                          No Transcriptions Available
                        </Typography>
                        <Typography variant="body1" color="text.primary">
                          {selectedCriteria
                            ? "There are no transcriptions assigned to you that match this criteria's purposes."
                            : 'Please select a criteria to view transcriptions.'}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </>
        )}

        <LabellerAssignmentsDrawer
          open={isAssignmentsDrawerOpen}
          onClose={() => setIsAssignmentsDrawerOpen(false)}
          assignments={userAssignments}
          criteria={criteria}
          transcriptions={transcriptions}
          essays={essays}
          purposes={purposes}
          criteriaPurposes={criteriaPurposes}
          essayPurposes={essayPurposes}
          onSelectAssignment={handleSelectAssignmentFromDrawer}
          onAssignmentsUpdated={handleAssignmentsUpdatedFromDrawer}
          showNotification={(message, severity) =>
            setNotification({ open: true, message, severity })
          }
        />

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={hideNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={hideNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
}

export default App;
